import { createContext, useContext, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import type { HreflangAlternate } from './seo'
import i18n from '@/i18n'
import { resolveLocale } from '@/i18n/supportedLocales'
import { useAuthStore } from '@/shared/store/authStore'

/**
 * Marketing DOCUMENTS speak two languages, not six.
 *
 * The app UI is translated into all six supported locales, but a marketing
 * document is a different artefact: it targets a MARKET, not a user preference.
 * A German comparison page has to stay German for an English browser (it is the
 * page Google ranks for "pixieset alternative" and the reader arrived from a
 * German query), and the claims discipline in WS1 means every sentence has to be
 * checkable — machine-translating the heavyweight compliance copy on /press and
 * /legal/subprocessors into four more languages nobody asked for would
 * manufacture exactly the unverifiable copy that pass exists to remove.
 *
 * So marketing content lives in typed content modules under
 * `features/landing/content/` rather than in the i18n namespaces, and this
 * module is the one place that maps a UI locale onto a marketing one.
 */
export type MarketingLocale = 'en' | 'de'

/**
 * The LANDING page speaks all seven (six since D3, Hungarian since RB-12 D26).
 *
 * Decision D3, 2026-08-19: the landing goes six-language while the paired
 * documents stay EN/DE. The split is not a compromise, it is the shape of the
 * two things — the landing is a **product surface** rendered in the visitor's
 * own language on a SINGLE URL (no pairs, no hreflang, no extra prerender
 * routes, no new OG cards), so widening it is a translation pass rather than an
 * architecture change. A document is a market-targeted URL and every extra
 * language there is a second indexable page carrying compliance claims.
 *
 * D26 (2026-09-28) adds ONE exception on purpose: `/hu`, the first
 * market-targeted landing URL — the same page with Hungarian forced by the
 * route, prerendered, with its own canonical, card and hreflang (see
 * `MARKET_LANDINGS` in marketingPages.ts). `/welcome` itself is unchanged.
 *
 * `MarketingLocale` is a subset, deliberately: a route-derived 'en' | 'de' is
 * assignable straight into a landing-locale slot, which is what lets the shared
 * chrome (nav, footer, seats counter) take its language from the URL on a
 * document and from the visitor on the landing without a second component.
 */
export type LandingLocale = MarketingLocale | 'fr' | 'it' | 'es' | 'pt' | 'hu' | 'nl' | 'pl' | 'cs'

/**
 * Iteration order — tests and the language selector read this.
 *
 * `hu` is the seventh (RB-12 C4, decision D26). Its copy is AI-drafted under
 * the RB-12 calibration and pending professional language review (IN-205).
 */
export const LANDING_LOCALES: readonly LandingLocale[] = ['en', 'de', 'fr', 'it', 'es', 'pt', 'hu', 'nl', 'pl', 'cs'] as const

/**
 * Landing languages whose content slots are still English placeholders.
 *
 * The "every non-English slot differs from English" test waives these, and
 * ONLY these; it also asserts each one is still English, so the entry fails
 * the day the translation lands and has to be deleted rather than lingering as
 * a silent licence to ship English under a Hungarian `lang`. Same shape as
 * `PENDING_TRANSLATION` in the i18n locale-coverage test.
 *
 * Empty since the Hungarian copy landed (RB-12 C4). Keep it empty: a new
 * landing language goes in here only for the commit that adds its slots.
 */
export const LANDING_PENDING_TRANSLATION: readonly LandingLocale[] = []

/** A piece of marketing copy that exists in both marketing languages. */
export type Bilingual<T> = Record<MarketingLocale, T>

/**
 * A piece of landing copy, which exists in all six.
 *
 * This type IS the translation contract: widening a landing content module to
 * `Multilingual<T>` makes the compiler enumerate every string that still needs a
 * language, so nothing can be forgotten silently the way an i18n JSON key can.
 */
export type Multilingual<T> = Record<LandingLocale, T>

/**
 * de-CH (and any other German tag) reads German; everything else reads English.
 * English is the fallback rather than a "closest match" because a Swiss-French
 * or Italian visitor is better served by the language the product was authored
 * in than by German they may not read.
 *
 * Also the narrowing step from a `LandingLocale` back down to the two: the
 * landing footer of an Italian visitor links to the ENGLISH /press, because that
 * is the nearest document that exists. Passing a LandingLocale here is valid —
 * it is a language tag like any other.
 */
export function toMarketingLocale(language: string | undefined | null): MarketingLocale {
  return language?.toLowerCase().startsWith('de') ? 'de' : 'en'
}

/** Marketing language for the two-language surfaces that follow the visitor. */
export function useMarketingLocale(): MarketingLocale {
  const { i18n } = useTranslation()
  return toMarketingLocale(i18n.language)
}

/**
 * The six app UI locales, one-to-one onto the six landing languages.
 *
 * Prefix-matched rather than exact, because the tag can arrive from the browser
 * as `pt`, `pt-PT` or `fr-FR` and the landing has one variant per language.
 * Anything we do not serve falls back to English — the language the product is
 * authored in — never to a "closest" neighbour.
 */
export function toLandingLocale(language: string | undefined | null): LandingLocale {
  const prefix = language?.toLowerCase().split('-')[0]
  return (LANDING_LOCALES as readonly string[]).includes(prefix ?? '')
    ? (prefix as LandingLocale)
    : 'en'
}

/**
 * The visitor's explicit landing-language choice, under its OWN key.
 *
 * Deliberately NOT `studioflow.uiLocale` (i18next's detector cache) and never
 * `i18n.changeLanguage()`: the browser reading /welcome may also be logged in
 * as a studio member, and their admin uiLocale is not marketing's to overwrite
 * — the same footgun `MarketingLanguageSwitch` documents. A separate key means
 * the landing selector can only ever affect the landing.
 */
const LANDING_LOCALE_KEY = 'studioflow.landingLocale'
/** Same-tab notification channel — `storage` events only fire on OTHER tabs. */
const LANDING_LOCALE_EVENT = 'studioflow:landing-locale'

function readOverride(): LandingLocale | null {
  try {
    const raw = window.localStorage.getItem(LANDING_LOCALE_KEY)
    return raw != null && (LANDING_LOCALES as readonly string[]).includes(raw)
      ? (raw as LandingLocale)
      : null
  } catch {
    return null // storage blocked (private mode / iframe) — fall back to detection
  }
}

/** i18next detector cache — the app UI language (see `i18n/index.ts`). */
const UI_LOCALE_KEY = 'studioflow.uiLocale'

/**
 * W1 (r12): a visitor who picks Español on the landing and then clicks
 * "Start free" must meet a Spanish /register, and the registration payload
 * must carry `es-ES`. The landing key is separate on purpose, so the choice is
 * ALSO seeded into the app UI language, but only when that is safe: nothing is
 * stored under `studioflow.uiLocale` (no member preference to overwrite) and
 * nobody is signed in (a stored value equal to the browser's detected language
 * counts as absent, see below). Exported for the unit test.
 */
export function seedUiLocaleFromLanding(locale: LandingLocale): boolean {
  try {
    if (useAuthStore.getState().accessToken) return false
    // The i18next detector caches what it detected on first load, so "key
    // present" alone would make this rule dead. A stored value that is just the
    // browser's own language is a default, not a choice; anything else is a
    // preference and stays.
    const stored = window.localStorage.getItem(UI_LOCALE_KEY)
    if (stored != null && resolveLocale(stored) !== resolveLocale(window.navigator.language)) return false
    const bundle = resolveLocale(locale)
    window.localStorage.setItem(UI_LOCALE_KEY, bundle)
    void i18n.changeLanguage(bundle)
    return true
  } catch {
    return false
  }
}

export function setLandingLocaleOverride(locale: LandingLocale): void {
  try {
    window.localStorage.setItem(LANDING_LOCALE_KEY, locale)
  } catch {
    /* storage blocked — the event below still switches this visit */
  }
  seedUiLocaleFromLanding(locale)
  window.dispatchEvent(new Event(LANDING_LOCALE_EVENT))
}

function subscribeToOverride(onChange: () => void): () => void {
  window.addEventListener(LANDING_LOCALE_EVENT, onChange)
  window.addEventListener('storage', onChange)
  return () => {
    window.removeEventListener(LANDING_LOCALE_EVENT, onChange)
    window.removeEventListener('storage', onChange)
  }
}

/**
 * Landing language for the visitor: their explicit selector choice if they made
 * one, otherwise i18n's detected language.
 *
 * Read-only towards i18n, like `useMarketingLocale`: it never writes a language
 * back. The override lives in `useSyncExternalStore` so every landing component
 * (all ten read this hook) re-renders together when the selector changes.
 */
export function useLandingLocale(): LandingLocale {
  const { i18n } = useTranslation()
  const override = useSyncExternalStore(subscribeToOverride, readOverride)
  const forced = useContext(LandingRouteContext)
  return forced?.locale ?? override ?? toLandingLocale(i18n.language)
}

/**
 * A market-targeted landing URL (decision D26): the SAME landing page, served
 * on its own indexable path with its language decided by the route.
 *
 * `/welcome` (canonical `/`) stays what it was — one URL in the visitor's own
 * language. A market URL such as `/hu` exists for the opposite reason: Google
 * can only rank a Hungarian page for Hungarian queries if there is a URL whose
 * content is ALWAYS Hungarian, whatever browser the crawler or the visitor
 * brings. So on a market URL the route's locale beats both the visitor's
 * selector choice and the detected language.
 */
export interface LandingMarketRoute {
  /** The route as served and canonicalised, e.g. `/hu`. */
  path: string
  locale: LandingLocale
}

/**
 * Set by `LandingPageV5` on a market URL, null everywhere else — so every
 * landing component that reads `useLandingLocale()` follows the route without
 * a prop being threaded through ten of them, and the nav's section links stay
 * on the market URL instead of dropping the visitor onto `/welcome`.
 */
export const LandingRouteContext = createContext<LandingMarketRoute | null>(null)

/** The market route the landing is being rendered on, or null on `/welcome`. */
export function useLandingRoute(): LandingMarketRoute | null {
  return useContext(LandingRouteContext)
}

/** Pick the marketing-language variant out of a bilingual content block. */
export function pick<T>(content: Bilingual<T>, locale: MarketingLocale): T {
  return content[locale]
}

/** The other marketing language — there are only two, so this is total. */
export function otherMarketingLocale(locale: MarketingLocale): MarketingLocale {
  return locale === 'de' ? 'en' : 'de'
}

/**
 * BCP-47 tag written to <html lang> per marketing language.
 *
 * de-CH rather than plain de: the German copy is Swiss German usage (MWST,
 * Offerte, dreissig) written for a Swiss market, and the hreflang has to say so
 * or a reader in Germany gets served a page whose tax vocabulary is not theirs.
 */
export const MARKETING_HTML_LANG: Bilingual<string> = { en: 'en', de: 'de-CH' }

/**
 * BCP-47 tag written to <html lang> per LANDING language.
 *
 * The Swiss tags for the same reason as above (de-CH, fr-CH, it-CH: the copy
 * uses Swiss tax vocabulary — MWST/TVA/IVA at 8.1% and the QR-bill), and the
 * app's own regional choices for the two that are not Swiss markets: es-ES and
 * pt-BR, matching `SUPPORTED_LOCALES`. On `/welcome` this only ever describes
 * the document a visitor is actually being shown; the one hreflang set the
 * landing carries (`LANDING_HREFLANG`) is about the market URLs, not these.
 */
export const LANDING_HTML_LANG: Multilingual<string> = {
  en: 'en',
  de: 'de-CH',
  fr: 'fr-CH',
  it: 'it-CH',
  es: 'es-ES',
  pt: 'pt-BR',
  // Plain `hu`, not hu-HU: the copy is written for Hungarian readers wherever
  // they are, and it is also the hreflang `/hu` declares.
  hu: 'hu',
  // RB-43: plain language tags for the three new markets; the landing has one
  // variant per language and, unlike hu, no market URL (so no hreflang).
  nl: 'nl',
  pl: 'pl',
  cs: 'cs',
}

/**
 * hreflang set for a page that exists as a pair of separate per-language URLs.
 *
 * BOTH variants emit the SAME set, including a self-reference. That is not
 * redundancy — reciprocity is the thing Google actually verifies, and an
 * annotation that points away without pointing back is ignored wholesale.
 *
 * x-default is the English URL: it is the language the product is authored in,
 * so it is the better landing for a visitor whose language we do not serve
 * (a French or Italian speaker) than German they may not read.
 */
export function hreflangPair(paths: Bilingual<string>): HreflangAlternate[] {
  return [
    { hreflang: MARKETING_HTML_LANG.en, href: paths.en },
    { hreflang: MARKETING_HTML_LANG.de, href: paths.de },
    { hreflang: 'x-default', href: paths.en },
  ]
}
